import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    FiActivity,
    FiPlus,
    FiCheck,
    FiEdit2,
    FiTrash2,
    FiInfo,
    FiMaximize2,
    FiX,
} from 'react-icons/fi';
import { GiBabyBottle } from 'react-icons/gi';
import { FaChild } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { jsPDF } from 'jspdf';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useChildProfile } from './ChildProfileLayout';
import { useGrowth } from '../../hooks/useGrowth';
import {
    PageHeader,
    ConfirmDialog,
    EmptyState,
    SkeletonChart,
    SkeletonTableRow,
    SkeletonTabs,
    useToast,
    AgeCategoryCard,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { parseApiError } from '../../api/errors';
import {
    formatDateDisplay,
    calculateAge,
    decimalAgeYears,
} from '../../utils/dates';
import { runPdfTable } from '../../utils/pdfReport';
import DateInput from '../Common/DateInput';
import {
    WHO_WEIGHT_GIRLS,
    WHO_WEIGHT_BOYS,
    WHO_LENGTH_GIRLS_0_24,
    WHO_LENGTH_BOYS_0_24,
    WHO_WEIGHT_LENGTH_GIRLS,
    WHO_WEIGHT_LENGTH_BOYS,
    WHO_HEIGHT_GIRLS_24_60,
    WHO_HEIGHT_BOYS_24_60,
} from '../../data/growthStandards';
import './ChildProfile.css';
import './Growth.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

const ALL_SUBTABS = [
    { key: 'early', label: 'Early Childhood', range: '0 – 2 Years', icon: GiBabyBottle, accent: 'sky' },
    { key: 'preschool', label: 'Preschool Age', range: '2 – 5 Years', icon: FaChild, accent: 'emerald' },
];

/**
 * CDSS: return only the subtabs appropriate for the child's age.
 *
 *   age <= 2 yrs  → only "Early Childhood"
 *   age > 2 yrs   → "Early Childhood" + "Preschool Age" (auto-pick
 *                   "Preschool Age" since the child has grown out of 0-2)
 *
 * The 5–11 yr "school age" charts are no longer offered — the WHO standards
 * band we track ends at 5 yrs. The PHM can still record measurements for
 * older children; the table at the bottom keeps the full history.
 */
const getVisibleSubTabs = (dob) => {
    const years = decimalAgeYears(dob);
    if (years <= 2) return [ALL_SUBTABS[0]];
    return ALL_SUBTABS;
};

// pickSubTab is intentionally kept as documentation of the CDSS rule.
// The auto-select is performed inline in the profile-load effect below.
// eslint-disable-next-line no-unused-vars
const pickSubTab = (dob) => {
    const visible = getVisibleSubTabs(dob);
    // Children > 2 yrs default to "Preschool Age" so the PHM sees the
    // age-appropriate chart without an extra click.
    if (visible.length > 1) return visible[1].key;
    return visible[0].key;
};

const getAgeInMonthsFloat = (dobStr, dateStr) => {
    if (!dobStr || !dateStr) return 0;
    const dob = new Date(dobStr);
    const d = new Date(dateStr);
    if (Number.isNaN(dob.getTime()) || Number.isNaN(d.getTime())) return 0;
    const days = (d.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, parseFloat((days / 30.4375).toFixed(2)));
};

/**
 * Right-side SD labels — draws each WHO reference curve's label OUTSIDE
 * the chart's plot area, in the canvas's right padding strip, anchored
 * to the y-coordinate of each line's last point.
 *
 *   • Dataset 0 is the child's own line — left alone; its point markers
 *     already mark the most recent measurement.
 *   • SD lines (+3 SD, +2 SD, …, Median, -1 SD, …, -3 SD) get a small
 *     colored label drawn just past the plot area's right edge.
 *   • Vertical collision: a "last drawn y" guard nudges each label up
 *     by 12 px if it would overlap a previously drawn label.
 *   • On extremely narrow canvases (<180 px) we skip labels entirely;
 *     they wouldn't fit anyway and would clip the canvas.
 */
const RIGHT_LABEL_VERTICAL_GAP = 12;
const RIGHT_LABEL_MIN_CANVAS_WIDTH = 180;

const rightLineLabelsPlugin = {
    id: 'rightLineLabels',
    afterDatasetsDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        // Canvas width = the full <canvas> element's display width in CSS px.
        const canvasWidth = chart.width;
        if (canvasWidth < RIGHT_LABEL_MIN_CANVAS_WIDTH) return;

        ctx.save();
        ctx.font = "600 10px 'Inter', system-ui, sans-serif";
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';

        const lastYs = [];
        const overlaps = (candidateY) =>
            lastYs.some((y) => Math.abs(y - candidateY) < RIGHT_LABEL_VERTICAL_GAP);

        chart.data.datasets.forEach((ds, i) => {
            if (i === 0) return; // skip the child's own line
            const meta = chart.getDatasetMeta(i);
            if (!meta?.data?.length) return;
            const lastPt = meta.data[meta.data.length - 1];
            if (!lastPt) return;
            let labelY = lastPt.y;
            // nudge up if we'd overlap a label already drawn at this chart side
            for (let n = 0; n < 6; n += 1) {
                if (!overlaps(labelY)) break;
                labelY -= RIGHT_LABEL_VERTICAL_GAP;
            }
            lastYs.push(labelY);

            // Anchor 6 px past the plot area's right edge, into the canvas's
            // right padding strip. Clamp to canvas width minus label width
            // so a long label doesn't get clipped.
            const textWidth = ctx.measureText(ds.label).width;
            const labelX = Math.min(
                chartArea.right + 6,
                canvasWidth - textWidth - 4,
            );
            ctx.fillStyle = ds.borderColor || '#475569';
            ctx.fillText(ds.label, labelX, labelY);
        });
        ctx.restore();
    },
};

const Growth = () => {
    const { id } = useParams();
    const toast = useToast();

    const { data: profile, setPdfExporter } = useChildProfile();
    const { records: apiRecords, loading, create, update: updateRecord, remove } = useGrowth(id);

    // Stable named refs for each chart instance. react-chartjs-2 v5 only
    // fires the ref callback on mount/unmount of <Line> (chart.update() runs
    // in place on prop changes), so useRef — which is also only written on
    // mount/unmount — keeps .current pointing at the live chart across
    // re-renders. The early tab mounts 3 charts at positions 0/1/2; the
    // preschool tab reuses positions 0/1 and leaves 2 unused.
    const chartRef0 = useRef(null); // Weight-for-Age (in both tabs)
    const chartRef1 = useRef(null); // Length/Height-for-Age (in both tabs)
    const chartRef2 = useRef(null); // Weight-for-Length (early only)

    // Ref to the Growth History table — used by the "+ Record" header
    // button to scroll the inline add-row into view.
    const historyTableRef = useRef(null);

    const [growthRecords, setGrowthRecords] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showAddRow, setShowAddRow] = useState(false);
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [newRecord, setNewRecord] = useState({
        date: '',
        age: '',
        weight: '',
        height: '',
        remarks: '',
    });
    const [confirmDialog, setConfirmDialog] = useState(null);

    const [growthSubTab, setGrowthSubTab] = useState('early');
    const [cdssAutoSelected, setCdssAutoSelected] = useState(false);
    const [visibleSubTabs, setVisibleSubTabs] = useState(ALL_SUBTABS);
    // { title, data, options } of the chart currently shown fullscreen, or null
    const [expandedChart, setExpandedChart] = useState(null);

    // CDSS: compute the age-appropriate subtab list and auto-pick the
    // appropriate tab when the profile first loads. If the user has already
    // navigated manually we don't override their choice, but if the active
    // tab is no longer visible (e.g. age changed), we fall back to the
    // first visible one. For children > 2 yrs we auto-pick "Preschool Age"
    // since the 0-2 charts are no longer the primary interest.
    useEffect(() => {
        if (profile && profile.dob && !cdssAutoSelected) {
            const visible = getVisibleSubTabs(profile.dob);
            setVisibleSubTabs(visible);
            const initialKey = visible.length > 1 ? visible[1].key : visible[0].key;
            setGrowthSubTab(initialKey);
            setCdssAutoSelected(true);
        }
    }, [profile, cdssAutoSelected]);

    // Defensive: if the active tab falls off the visible list, fall back
    // to the first visible one. (Covers the edge case where a child's
    // record was created at one age and then loaded with a different dob.)
    useEffect(() => {
        if (visibleSubTabs.length === 0) return;
        if (!visibleSubTabs.some((t) => t.key === growthSubTab)) {
            setGrowthSubTab(visibleSubTabs[0].key);
        }
    }, [visibleSubTabs, growthSubTab]);

    // Sync API records
    useEffect(() => {
        if (apiRecords && apiRecords.length > 0 && growthRecords.length === 0) {
            setGrowthRecords(apiRecords);
        }
    }, [apiRecords, growthRecords.length]);

    // Warn before unload
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    const sortedRecords = useMemo(() => {
        return [...growthRecords]
            .filter((r) => r.date)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [growthRecords]);

    const childAgeYears = useMemo(
        () => (profile?.dob ? decimalAgeYears(profile.dob) : 0),
        [profile?.dob],
    );
    const ageFilterLabel = useMemo(() => {
        if (growthSubTab === 'preschool') return 'filtered for 2 – 5 years';
        return 'filtered for ≤ 2 years';
    }, [growthSubTab]);

    // Memoize the chart datasets so the chart's data reference only changes
    // when an underlying value actually changes — not on every parent render.
    // Inline recomputation creates a fresh array reference every render even
    // for unrelated re-renders, and Chart.js's internal diff against the
    // previous datasets can miss single-point movements (the Wasting chart's
    // child point "doesn't move" when the user edits weight or height in
    // the table). With explicit deps on records + dob + sub-tab, the new
    // array reference is a clean signal that triggers a re-render.
    // ponytail: useMemo here is the narrowest fix; a heavier refactor would
    // also memoize `createChartData` and `createChartOptions` per render
    // with the same deps.
    const ageRange = useMemo(
        () => (growthSubTab === 'preschool' ? [24, 60] : [0, 24]),
        [growthSubTab],
    );
    const awData = useMemo(
        () => sortedRecords
            .filter((r) => r.weight && !Number.isNaN(parseFloat(r.weight)))
            .map((r) => ({ x: getAgeInMonthsFloat(profile?.dob, r.date), y: parseFloat(r.weight) }))
            .filter((pt) => pt.x >= ageRange[0] && pt.x <= ageRange[1]),
        [sortedRecords, profile?.dob, ageRange],
    );
    const ahData = useMemo(
        () => sortedRecords
            .filter((r) => r.height && !Number.isNaN(parseFloat(r.height)))
            .map((r) => ({ x: getAgeInMonthsFloat(profile?.dob, r.date), y: parseFloat(r.height) }))
            .filter((pt) => pt.x >= ageRange[0] && pt.x <= ageRange[1]),
        [sortedRecords, profile?.dob, ageRange],
    );
    const wlData = useMemo(
        () => sortedRecords
            .filter(
                (r) =>
                    r.weight &&
                    r.height &&
                    !Number.isNaN(parseFloat(r.weight)) &&
                    !Number.isNaN(parseFloat(r.height)),
            )
            .filter((r) => getAgeInMonthsFloat(profile?.dob, r.date) <= 24)
            .map((r) => ({ x: parseFloat(r.height), y: parseFloat(r.weight) }))
            .sort((a, b) => a.x - b.x),
        [sortedRecords, profile?.dob],
    );

    const handleAddRecord = useCallback(async () => {
        if (!newRecord.date) {
            toast.error('Please enter a date.');
            return;
        }
        try {
            const payload = {
                date: newRecord.date,
                age: newRecord.age || calculateAge(profile.dob, newRecord.date),
                weight: newRecord.weight,
                height: newRecord.height,
                remarks: newRecord.remarks,
            };
            const created = await create(payload);
            setGrowthRecords((prev) => [...prev, created]);
            setNewRecord({ date: '', age: '', weight: '', height: '', remarks: '' });
            setShowAddRow(false);
            setHasUnsavedChanges(false);
            toast.success('Growth record added.');
        } catch (err) {
            toast.error(parseApiError(err, 'Failed to add growth record.'));
        }
    }, [newRecord, profile, create, toast]);

    const handleFieldChange = (recordId, field, value) => {
        setHasUnsavedChanges(true);
        setGrowthRecords((prev) =>
            prev.map((r) => (r.id === recordId ? { ...r, [field]: value } : r)),
        );
    };

    const handleSaveEdit = useCallback(
        async (recordId) => {
            const record = growthRecords.find((r) => r.id === recordId);
            if (!record) return;
            try {
                await updateRecord(recordId, record);
                setEditingRecordId(null);
                setHasUnsavedChanges(false);
                toast.success('Growth record updated.');
            } catch (err) {
                toast.error(parseApiError(err, 'Failed to update record.'));
            }
        },
        [growthRecords, updateRecord, toast],
    );

    const handleDeleteRecord = useCallback(
        (recordId) => {
            setConfirmDialog({
                title: 'Delete this growth record?',
                body: 'This measurement will be permanently removed. This cannot be undone.',
                confirmLabel: 'Delete',
                confirmSeverity: 'error',
                onConfirm: async () => {
                    try {
                        await remove(recordId);
                        setGrowthRecords((prev) => prev.filter((r) => r.id !== recordId));
                        setHasUnsavedChanges(false);
                        setConfirmDialog(null);
                        toast.success('Record deleted.');
                    } catch (err) {
                        toast.error(parseApiError(err, 'Failed to delete record.'));
                        setConfirmDialog(null);
                    }
                },
                onClose: () => setConfirmDialog(null),
            });
        },
        [remove, toast],
    );

    const generatePDF = useCallback(() => {
        if (!profile) {
            toast.error('Profile data is not available yet.');
            return;
        }
        if (sortedRecords.length === 0) {
            toast.warning('No growth records to export.');
            return;
        }

        const sectionLabel = growthSubTab === 'early' ? 'Early Childhood' : 'Preschool Age';

        const doc = new jsPDF('landscape');
        doc.setFontSize(22);
        doc.setTextColor(15, 23, 42);
        doc.text(`Growth History Report — ${sectionLabel}`, 14, 22);

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Child Name: ${profile.name}`, 14, 32);
        doc.text(`Reg Number: ${profile.childNo}`, 14, 38);
        doc.text(`DOB: ${formatDateDisplay(profile.dob)}`, 100, 32);
        doc.text(`Current Age: ${calculateAge(profile.dob)}`, 100, 38);
        doc.text(`Gender: ${profile.gender}`, 180, 32);

        // Charts — embed the active subtab's charts as PNGs above the table.
        // Each subtab has its own set of charts at fixed DOM positions; we
        // read the live chart instances from the named refs the <Line>
        // components were assigned. Refs are populated by react-chartjs-2 on
        // mount/unmount only, so the values are stable across re-renders.
        const sectionCharts = growthSubTab === 'early'
            ? [
                { node: chartRef0.current, title: 'Weight-for-Age (0–24 Months)' },
                { node: chartRef1.current, title: 'Length-for-Age (0–24 Months)' },
                { node: chartRef2.current, title: 'Weight-for-Length (Wasting)' },
            ]
            : [
                { node: chartRef0.current, title: 'Weight-for-Age (24–60 Months)' },
                { node: chartRef1.current, title: 'Height-for-Age (24–60 Months)' },
            ];

        // Landscape A4 gives ~282mm usable width; place two charts per row
        // (132mm each, 8mm gap). 3 charts overflow page 1 — push the third
        // (and the autoTable that follows) onto a new page.
        const charts = sectionCharts.filter((entry) => entry.node);
        const marginX = 14;
        const chartW = 132;
        const chartH = 78;
        const gapX = 8;
        const gapY = 18; // room for title between rows

        // Lay `chartSubset` out as a 2-column grid starting at `startY`;
        // returns the next cursor Y. Single-chart rows are centered.
        const drawChartRow = (chartSubset, startY) => {
            chartSubset.forEach((entry, i) => {
                const col = i % 2;
                const row = Math.floor(i / 2);
                const colCount = col === 0 && (chartSubset.length - (row * 2)) === 1
                    ? 1
                    : Math.min(2, chartSubset.length - row * 2);
                const x = colCount === 1
                    ? marginX + (chartW + gapX) / 2
                    : marginX + col * (chartW + gapX);
                const y = startY + row * (chartH + gapY);

                try {
                    const img = entry.node.toBase64Image('image/png', 1.0);
                    doc.setFontSize(11);
                    doc.setTextColor(15, 23, 42);
                    doc.text(entry.title || '', x, y + 6);
                    doc.addImage(img, 'PNG', x, y + 8, chartW, chartH);
                } catch (err) {
                    // ponytail: skip charts that aren't fully mounted yet; the
                    // table below still gets exported.
                }
            });
            const rowsUsed = Math.ceil(chartSubset.length / 2);
            return startY + rowsUsed * (chartH + gapY) + 6;
        };

        let cursorY = 46;
        if (charts.length === 3) {
            cursorY = drawChartRow([charts[0], charts[1]], cursorY);
            doc.addPage();
            cursorY = 22;
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Continued — ${sectionLabel}`, 14, 14);
            cursorY = drawChartRow([charts[2]], cursorY);
        } else if (charts.length > 0) {
            cursorY = drawChartRow(charts, cursorY);
        }

        const columns = ['Date', 'Age', 'Weight (kg)', 'Length (cm)', 'Remarks'];
        const rows = sortedRecords.map((r) => [
            formatDateDisplay(r.date),
            r.age || '-',
            r.weight || '-',
            r.height || '-',
            r.remarks || '-',
        ]);

        runPdfTable({
            doc,
            startY: cursorY,
            head: columns,
            body: rows,
            accent: [14, 165, 233],
        });

        const stamp = new Date().toISOString().split('T')[0];
        doc.save(`${profile.childNo}_Growth_Report_${stamp}.pdf`);
        toast.success('PDF exported.');
    }, [profile, sortedRecords, toast, growthSubTab]);

    // Register this tab's PDF exporter with the layout so the header's
    // "Export PDF" button calls it.
    useEffect(() => {
        setPdfExporter(() => generatePDF());
        return () => setPdfExporter(null);
    }, [setPdfExporter, generatePDF]);

    // Chart helpers
    const createChartData = (label, childData, whoData, lineColor = '#0ea5e9') => {
        if (!whoData) return { datasets: [] };
        const isLengthX = whoData.length > 0 && 'length' in whoData[0];
        const getX = (d) => (isLengthX ? d.length : d.month);
        return {
            datasets: [
                {
                    label,
                    data: childData,
                    borderColor: lineColor,
                    backgroundColor: `${lineColor}1A`,
                    borderWidth: 2.5,
                    pointBackgroundColor: lineColor,
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    tension: 0.3,
                    fill: true,
                },
                {
                    label: '+3 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdPlus3 })),
                    borderColor: '#b91c1c',
                    borderWidth: 1.2,
                    borderDash: [2, 3],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: '+2 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdPlus2 })),
                    borderColor: '#ea580c',
                    borderWidth: 1.2,
                    borderDash: [4, 4],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: '+1 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdPlus1 })),
                    borderColor: '#eab308',
                    borderWidth: 1.2,
                    borderDash: [4, 4],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: 'Median',
                    data: whoData.map((d) => ({ x: getX(d), y: d.median })),
                    borderColor: '#22c55e',
                    borderWidth: 1.5,
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: '-1 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdMinus1 })),
                    borderColor: '#eab308',
                    borderWidth: 1.2,
                    borderDash: [4, 4],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: '-2 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdMinus2 })),
                    borderColor: '#ea580c',
                    borderWidth: 1.2,
                    borderDash: [4, 4],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
                {
                    label: '-3 SD',
                    data: whoData.map((d) => ({ x: getX(d), y: d.sdMinus3 })),
                    borderColor: '#b91c1c',
                    borderWidth: 1.2,
                    borderDash: [2, 3],
                    pointRadius: 0,
                    fill: false,
                    tension: 0.4,
                },
            ],
        };
    };

    const createChartOptions = (xTitle, yTitle, xMin, xMax, xStep, yUnit) => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        layout: {
            // right-side SD labels live in this padding strip, just past the
            // plot area's right edge. 60px fits the longest label ("+3 SD")
            // with a small breathing margin.
            padding: { right: 60 },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#0f172a',
                titleFont: { family: "'Inter', sans-serif" },
                bodyFont: { family: "'Inter', sans-serif" },
                padding: 10,
                cornerRadius: 6,
                callbacks: {
                    title: (items) => `${xTitle}: ${items[0].parsed.x}`,
                    label: (item) => `${item.dataset.label}: ${item.parsed.y} ${yUnit}`,
                },
            },
        },
        scales: {
            x: {
                type: 'linear',
                min: xMin,
                max: xMax,
                title: {
                    display: true,
                    text: xTitle,
                    font: { family: "'Inter', sans-serif", size: 11, weight: 600 },
                    color: '#475569',
                },
                grid: { color: 'rgba(148, 163, 184, 0.18)' },
                ticks: {
                    font: { family: "'Inter', sans-serif" },
                    color: '#64748b',
                    stepSize: xStep,
                },
            },
            y: {
                title: {
                    display: true,
                    text: yTitle,
                    font: { family: "'Inter', sans-serif", size: 11, weight: 600 },
                    color: '#475569',
                },
                grid: { color: 'rgba(148, 163, 184, 0.18)' },
                ticks: { font: { family: "'Inter', sans-serif" }, color: '#64748b' },
            },
        },
    });

    if (loading || !profile) {
        return (
            <div>
                <div className="cp-skeleton-stack">
                    <div className="pt-skeleton-header-card" />
                    <SkeletonTabs count={3} />
                    <div className="growth-skeleton-grid">
                        <SkeletonChart height={260} />
                        <SkeletonChart height={260} />
                    </div>
                    <div className="vacc-card">
                        <table className="vacc-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Age</th>
                                    <th>Weight</th>
                                    <th>Height</th>
                                    <th>Remarks</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <SkeletonTableRow key={i} cols={6} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    const isFemale = profile.gender?.toLowerCase() === 'female';

    let chartGrid;
    if (growthSubTab === 'early') {
        const whoWeight = isFemale
            ? WHO_WEIGHT_GIRLS.filter((d) => d.month <= 24)
            : WHO_WEIGHT_BOYS.filter((d) => d.month <= 24);
        const whoLength = isFemale ? WHO_LENGTH_GIRLS_0_24 : WHO_LENGTH_BOYS_0_24;
        const whoWeightLength = isFemale
            ? WHO_WEIGHT_LENGTH_GIRLS
            : WHO_WEIGHT_LENGTH_BOYS;

        chartGrid = (
            <div className="charts-grid-container three-charts">
                <div className="growth-chart-card">
                    <div className="vacc-card-header growth-chart-card-header">
                        <h2>Weight-for-Age (0–24 Months)</h2>
                        <button
                            type="button"
                            className="growth-chart-expand-btn"
                            onClick={() => setExpandedChart({
                                title: 'Weight-for-Age (0–24 Months)',
                                data: createChartData('Weight (kg)', awData, whoWeight, '#0ea5e9'),
                                options: createChartOptions('Age (months)', 'Weight (kg)', 0, 24, 3, 'kg'),
                            })}
                            aria-label="View chart fullscreen"
                            title="View fullscreen"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    </div>
                    <div className="growth-chart-container">
                        <Line
                            ref={chartRef0}
                            data={createChartData('Weight (kg)', awData, whoWeight, '#0ea5e9')}
                            options={createChartOptions('Age (months)', 'Weight (kg)', 0, 24, 3, 'kg')}
                            plugins={[rightLineLabelsPlugin]}
                        />
                    </div>
                </div>
                <div className="growth-chart-card">
                    <div className="vacc-card-header growth-chart-card-header">
                        <h2>Length-for-Age (0–24 Months)</h2>
                        <button
                            type="button"
                            className="growth-chart-expand-btn"
                            onClick={() => setExpandedChart({
                                title: 'Length-for-Age (0–24 Months)',
                                data: createChartData('Length (cm)', ahData, whoLength, '#10b981'),
                                options: createChartOptions('Age (months)', 'Length (cm)', 0, 24, 3, 'cm'),
                            })}
                            aria-label="View chart fullscreen"
                            title="View fullscreen"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    </div>
                    <div className="growth-chart-container">
                        <Line
                            ref={chartRef1}
                            data={createChartData('Length (cm)', ahData, whoLength, '#10b981')}
                            options={createChartOptions('Age (months)', 'Length (cm)', 0, 24, 3, 'cm')}
                            plugins={[rightLineLabelsPlugin]}
                        />
                    </div>
                </div>
                <div className="growth-chart-card">
                    <div className="vacc-card-header growth-chart-card-header">
                        <h2>Weight-for-Length (Wasting)</h2>
                        <button
                            type="button"
                            className="growth-chart-expand-btn"
                            onClick={() => setExpandedChart({
                                title: 'Weight-for-Length (Wasting)',
                                data: createChartData('Weight (kg)', wlData, whoWeightLength, '#ec4899'),
                                options: createChartOptions('Length (cm)', 'Weight (kg)', 45, 110, 10, 'kg'),
                            })}
                            aria-label="View chart fullscreen"
                            title="View fullscreen"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    </div>
                    <div className="growth-chart-container">
                        <Line
                            ref={chartRef2}
                            data={createChartData('Weight (kg)', wlData, whoWeightLength, '#ec4899')}
                            options={createChartOptions('Length (cm)', 'Weight (kg)', 45, 110, 10, 'kg')}
                            plugins={[rightLineLabelsPlugin]}
                        />
                    </div>
                </div>
            </div>
        );
    } else if (growthSubTab === 'preschool') {
        const whoWeight = isFemale
            ? WHO_WEIGHT_GIRLS.filter((d) => d.month >= 24 && d.month <= 60)
            : WHO_WEIGHT_BOYS.filter((d) => d.month >= 24 && d.month <= 60);
        const whoHeight = isFemale ? WHO_HEIGHT_GIRLS_24_60 : WHO_HEIGHT_BOYS_24_60;

        chartGrid = (
            <div className="charts-grid-container two-charts">
                <div className="growth-chart-card">
                    <div className="vacc-card-header growth-chart-card-header">
                        <h2>Weight-for-Age (24–60 Months)</h2>
                        <button
                            type="button"
                            className="growth-chart-expand-btn"
                            onClick={() => setExpandedChart({
                                title: 'Weight-for-Age (24–60 Months)',
                                data: createChartData('Weight (kg)', awData, whoWeight, '#0ea5e9'),
                                options: createChartOptions('Age (months)', 'Weight (kg)', 24, 60, 6, 'kg'),
                            })}
                            aria-label="View chart fullscreen"
                            title="View fullscreen"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    </div>
                    <div className="growth-chart-container">
                        <Line
                            ref={chartRef0}
                            data={createChartData('Weight (kg)', awData, whoWeight, '#0ea5e9')}
                            options={createChartOptions('Age (months)', 'Weight (kg)', 24, 60, 6, 'kg')}
                            plugins={[rightLineLabelsPlugin]}
                        />
                    </div>
                </div>
                <div className="growth-chart-card">
                    <div className="vacc-card-header growth-chart-card-header">
                        <h2>Height-for-Age (24–60 Months)</h2>
                        <button
                            type="button"
                            className="growth-chart-expand-btn"
                            onClick={() => setExpandedChart({
                                title: 'Height-for-Age (24–60 Months)',
                                data: createChartData('Height (cm)', ahData, whoHeight, '#10b981'),
                                options: createChartOptions('Age (months)', 'Height (cm)', 24, 60, 6, 'cm'),
                            })}
                            aria-label="View chart fullscreen"
                            title="View fullscreen"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    </div>
                    <div className="growth-chart-container">
                        <Line
                            ref={chartRef1}
                            data={createChartData('Height (cm)', ahData, whoHeight, '#10b981')}
                            options={createChartOptions('Age (months)', 'Height (cm)', 24, 60, 6, 'cm')}
                            plugins={[rightLineLabelsPlugin]}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const cdssSubTab = visibleSubTabs.find((s) => s.key === growthSubTab);

    return (
        <div>
            <PageHeader
                title="Growth Monitoring"
                subtitle={`WHO Z-score reference curves · ${ageFilterLabel}`}
                icon={FiActivity}
                className="vacc-section-header"
            />

            <div className="growth-subtabs">
                <div className="growth-cdss-banner">
                    <div className="growth-cdss-banner__icon" aria-hidden="true">
                        <FiInfo size={14} />
                    </div>
                    <div className="growth-cdss-banner__body">
                        <strong>Showing {cdssSubTab?.label}</strong>
                        <span>
                            {visibleSubTabs.length === 1
                                ? 'Auto-selected — Early Childhood is the only WHO Z-score band we track for this child.'
                                : 'Auto-selected based on the child\'s age. Both Early Childhood and Preschool Age Z-score bands are available.'}
                        </span>
                    </div>
                </div>
                <div className="growth-subtab-row">
                    {visibleSubTabs.map((tab) => (
                        <AgeCategoryCard
                            key={tab.key}
                            tab={tab}
                            active={growthSubTab === tab.key}
                            autoSelected={tab.key === cdssSubTab?.key}
                            onClick={() => setGrowthSubTab(tab.key)}
                        />
                    ))}
                </div>
            </div>

            <div className="growth-charts-wrapper">{chartGrid}</div>

            <Dialog
                open={!!expandedChart}
                onClose={() => setExpandedChart(null)}
                maxWidth={false}
                fullWidth
                className="growth-chart-fullscreen-dialog"
                aria-labelledby="growth-chart-fullscreen-title"
            >
                {expandedChart && (
                    <>
                        <DialogTitle id="growth-chart-fullscreen-title" className="growth-chart-fullscreen-title">
                            <span>{expandedChart.title}</span>
                            <button
                                type="button"
                                className="growth-chart-expand-btn"
                                onClick={() => setExpandedChart(null)}
                                aria-label="Close"
                                title="Close"
                            >
                                <FiX size={18} />
                            </button>
                        </DialogTitle>
                        <DialogContent>
                            <div className="growth-chart-fullscreen-canvas">
                                <Line
                                    data={expandedChart.data}
                                    options={expandedChart.options}
                                    plugins={[rightLineLabelsPlugin]}
                                />
                            </div>
                        </DialogContent>
                    </>
                )}
            </Dialog>

            <div className="vacc-content">
                <div className="vacc-card">
                    <div className="vacc-card-header growth-history-header">
                        <h2>Growth History</h2>
                        <PageButton
                            type="button"
                            variant={showAddRow ? 'ghost' : 'primary'}
                            size="small"
                            startIcon={<FiPlus size={14} />}
                            disabled={showAddRow}
                            onClick={() => {
                                setShowAddRow(true);
                                setHasUnsavedChanges(true);
                                // Scroll the inline form into view on the next paint.
                                requestAnimationFrame(() => {
                                    historyTableRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                    });
                                });
                            }}
                        >
                            {showAddRow ? 'Adding record…' : 'Add record'}
                        </PageButton>
                    </div>
                    <div className="vacc-table-container" ref={historyTableRef}>
                        <table className="vacc-table growth-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Age</th>
                                    <th>Weight (kg)</th>
                                    <th>LENGTH / HEIGHT (CM)</th>
                                    <th>Remarks</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showAddRow && (
                                    <tr className="growth-add-row">
                                        <td>
                                            <DateInput
                                                className="vacc-date-input"
                                                value={newRecord.date || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const calcAge = calculateAge(profile.dob, val);
                                                    setNewRecord((prev) => ({
                                                        ...prev,
                                                        date: val,
                                                        age: calcAge || prev.age,
                                                    }));
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="vacc-text-input"
                                                placeholder="Age"
                                                value={newRecord.age || ''}
                                                onChange={(e) =>
                                                    setNewRecord((prev) => ({ ...prev, age: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                step="0.1"
                                                min="0"
                                                className="vacc-text-input"
                                                placeholder="e.g. 3.5"
                                                value={newRecord.weight}
                                                onChange={(e) =>
                                                    setNewRecord((prev) => ({ ...prev, weight: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                step="0.1"
                                                min="0"
                                                className="vacc-text-input"
                                                placeholder="e.g. 50"
                                                value={newRecord.height}
                                                onChange={(e) =>
                                                    setNewRecord((prev) => ({ ...prev, height: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="vacc-text-input"
                                                placeholder="Enter remark"
                                                value={newRecord.remarks}
                                                onChange={(e) =>
                                                    setNewRecord((prev) => ({ ...prev, remarks: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td>
                                            <div className="growth-action-btns">
                                                <PageButton
                                                    type="button"
                                                    size="small"
                                                    variant="success"
                                                    onClick={handleAddRecord}
                                                    title="Save"
                                                    sx={{ minWidth: 0, padding: '6px 8px' }}
                                                >
                                                    <FiCheck size={14} />
                                                </PageButton>
                                                <PageButton
                                                    type="button"
                                                    size="small"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setShowAddRow(false);
                                                        setNewRecord({
                                                            date: '',
                                                            age: '',
                                                            weight: '',
                                                            height: '',
                                                            remarks: '',
                                                        });
                                                    }}
                                                    title="Cancel"
                                                    sx={{ minWidth: 0, padding: '6px 8px' }}
                                                >
                                                    <FiTrash2 size={14} style={{ display: 'none' }} />
                                                    <span aria-hidden="true">✕</span>
                                                </PageButton>
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {sortedRecords.map((record) => {
                                    const isEditing = editingRecordId === record.id;
                                    return (
                                        <tr
                                            key={record.id}
                                            className={`${isEditing ? 'is-editing' : ''}`}
                                        >
                                            <td>
                                                {isEditing ? (
                                                    <DateInput
                                                        className="vacc-date-input"
                                                        value={record.date || ''}
                                                        onChange={(e) => {
                                                            const newDate = e.target.value;
                                                            const newAge = calculateAge(profile.dob, newDate);
                                                            setGrowthRecords((prev) =>
                                                                prev.map((r) =>
                                                                    r.id === record.id
                                                                        ? { ...r, date: newDate, age: newAge || r.age }
                                                                        : r,
                                                                ),
                                                            );
                                                            setHasUnsavedChanges(true);
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="growth-date-display">
                                                        {formatDateDisplay(record.date)}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="vacc-text-input"
                                                        value={
                                                            record.age ||
                                                            calculateAge(profile.dob, record.date) ||
                                                            ''
                                                        }
                                                        onChange={(e) =>
                                                            handleFieldChange(record.id, 'age', e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="vacc-text-input growth-text-display">
                                                        {record.age ||
                                                            calculateAge(profile.dob, record.date) ||
                                                            '-'}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="0.1"
                                                        min="0"
                                                        className="vacc-text-input"
                                                        value={record.weight || ''}
                                                        onChange={(e) =>
                                                            handleFieldChange(record.id, 'weight', e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="vacc-text-input growth-text-display">
                                                        {record.weight || '-'}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="0.1"
                                                        min="0"
                                                        className="vacc-text-input"
                                                        value={record.height || ''}
                                                        onChange={(e) =>
                                                            handleFieldChange(record.id, 'height', e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="vacc-text-input growth-text-display">
                                                        {record.height || '-'}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="vacc-text-input"
                                                        value={record.remarks || ''}
                                                        onChange={(e) =>
                                                            handleFieldChange(record.id, 'remarks', e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="vacc-text-input growth-text-display">
                                                        {record.remarks || '-'}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="growth-action-btns">
                                                    {isEditing ? (
                                                        <PageButton
                                                            type="button"
                                                            size="small"
                                                            variant="success"
                                                            onClick={() => handleSaveEdit(record.id)}
                                                            title="Save changes"
                                                            sx={{ minWidth: 0, padding: '6px 8px' }}
                                                        >
                                                            <FiCheck size={14} />
                                                        </PageButton>
                                                    ) : (
                                                        <PageButton
                                                            type="button"
                                                            size="small"
                                                            variant="ghost"
                                                            onClick={() => setEditingRecordId(record.id)}
                                                            title="Edit record"
                                                            sx={{ minWidth: 0, padding: '6px 8px' }}
                                                        >
                                                            <FiEdit2 size={14} />
                                                        </PageButton>
                                                    )}
                                                    <PageButton
                                                        type="button"
                                                        size="small"
                                                        variant="ghost"
                                                        onClick={() => handleDeleteRecord(record.id)}
                                                        title="Delete record"
                                                        sx={{
                                                            minWidth: 0,
                                                            padding: '6px 8px',
                                                            color: 'var(--pt-error)',
                                                            borderColor:
                                                                'color-mix(in srgb, var(--pt-error) 30%, transparent)',
                                                        }}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </PageButton>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {sortedRecords.length === 0 && !showAddRow && (
                                    <tr>
                                        <td colSpan={6} className="vacc-empty-cell">
                                            <EmptyState
                                                illustration="growth"
                                                compact
                                                title="No growth measurements yet"
                                                description="Add your first measurement to start tracking this child's growth."
                                                action={
                                                    <PageButton
                                                        type="button"
                                                        variant="primary"
                                                        startIcon={<FiPlus size={14} />}
                                                        onClick={() => setShowAddRow(true)}
                                                    >
                                                        Add measurement
                                                    </PageButton>
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {confirmDialog && (
                <ConfirmDialog
                    open
                    title={confirmDialog.title}
                    body={
                        confirmDialog.onDiscard ? (
                            <div>
                                <p>{confirmDialog.body}</p>
                                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                                    <PageButton
                                        type="button"
                                        variant="ghost"
                                        onClick={confirmDialog.onDiscard}
                                    >
                                        {confirmDialog.cancelLabel}
                                    </PageButton>
                                </div>
                            </div>
                        ) : (
                            confirmDialog.body
                        )
                    }
                    confirmLabel={confirmDialog.confirmLabel}
                    confirmSeverity={confirmDialog.confirmSeverity}
                    onConfirm={confirmDialog.onConfirm}
                    onClose={confirmDialog.onClose || (() => setConfirmDialog(null))}
                />
            )}
        </div>
    );
};

export default Growth;
